import { useEffect, useRef } from "react";
import { useStore } from "../state/store";
import { onPipelineStarted, onPipelineResult, onFinalGuess, onRoundReset } from "../ipc/events";

// Replace this with your actual Cloudflare Worker domain when deploying
export const RELAY_WS_URL = "wss://atlas-relay.zilverztream.workers.dev/ws";
export const RELAY_HTTP_URL = "https://atlas-relay.zilverztream.workers.dev/push";

const isTauri = typeof window !== "undefined" && window.hasOwnProperty("__TAURI_IPC__");

export function useRelay() {
  const applyStarted = useStore((s) => s.applyStarted);
  const applyResult = useStore((s) => s.applyResult);
  const setFinal = useStore((s) => s.setFinal);
  const setRoundNumber = useStore((s) => s.setRoundNumber);
  
  const ws = useRef<WebSocket | null>(null);

  useEffect(() => {
    // ----------------------------------------------------
    // SENDER (Laptop / Tauri)
    // ----------------------------------------------------
    if (isTauri) {
      // In Tauri: listen to local IPC events and forward them to the relay via POST.
      const unlistens: (() => void)[] = [];
      
      const pushEvent = (event: string, data: any) => {
        fetch(RELAY_HTTP_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ event, data }),
        }).catch(() => {});
      };

      onPipelineStarted((e) => pushEvent("pipeline://started", e)).then((u) => unlistens.push(u));
      onPipelineResult((e) => pushEvent("pipeline://result", e)).then((u) => unlistens.push(u));
      onFinalGuess((e) => pushEvent("state://final_guess", e)).then((u) => unlistens.push(u));
      onRoundReset(() => pushEvent("state://round_reset", null)).then((u) => unlistens.push(u));

      return () => {
        unlistens.forEach((u) => u());
      };
    } 
    
    // ----------------------------------------------------
    // RECEIVER (Phone / Web Browser)
    // ----------------------------------------------------
    else {
      // In Browser: connect to WebSocket and update Zustand store directly.
      console.log("Connecting to relay WebSocket...");
      ws.current = new WebSocket(RELAY_WS_URL);
      
      ws.current.onmessage = (msg) => {
        try {
          const payload = JSON.parse(msg.data);
          
          if (payload.type === "init") {
            // Restore state on connection
            if (payload.state["pipeline://started"]) applyStarted(payload.state["pipeline://started"]);
            if (payload.state["pipeline://result"]) applyResult(payload.state["pipeline://result"]);
            if (payload.state["state://final_guess"]) setFinal(payload.state["state://final_guess"]);
            return;
          }

          switch (payload.event) {
            case "pipeline://started":
              applyStarted(payload.data);
              break;
            case "pipeline://result":
              applyResult(payload.data);
              break;
            case "state://final_guess":
              setFinal(payload.data);
              break;
            case "state://round_reset":
              // We simulate a round reset here
              break;
          }
        } catch (e) {
          console.error("Relay message error", e);
        }
      };

      return () => {
        if (ws.current) {
          ws.current.close();
        }
      };
    }
  }, [applyStarted, applyResult, setFinal, setRoundNumber]);
}
